import { useLocation } from "wouter";
import { useForm, useFieldArray } from "react-hook-form";
import { zodFormResolver as zodResolver } from "@/lib/zodFormResolver";
import * as z from "zod";
import {
  useCreateSnippet,
  getListSnippetsQueryKey,
  type CreateSnippetInput,
} from "@workspace/api-client-react";
import { VARIABLE_TYPE_VALUES, VARIABLE_TYPE_LABELS } from "@/lib/enumValues";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Loader2, Plus, Trash2 } from "lucide-react";
import { Link } from "wouter";
import { apiErrorMessage } from "@/lib/apiError";

const variableSchema = z.object({
  key: z.string().min(1, "Key is required"),
  label: z.string().min(1, "Label is required"),
  type: z.enum(VARIABLE_TYPE_VALUES),
  required: z.boolean(),
  defaultValue: z.string().optional().nullable(),
  placeholder: z.string().optional().nullable(),
});

const formSchema = z.object({
  name: z.string().min(1, "Name is required").max(120),
  description: z.string().max(280),
  bodyHtml: z.string().min(1, "Body HTML is required"),
  variables: z.array(variableSchema),
});

export default function SnippetNew() {
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const createMutation = useCreateSnippet();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      description: "",
      bodyHtml: "",
      variables: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "variables",
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      const data: CreateSnippetInput = {
        name: values.name,
        description: values.description,
        bodyHtml: values.bodyHtml,
        variables: values.variables,
      };
      const res = await createMutation.mutateAsync({ data });
      toast.success("Snippet created");
      queryClient.invalidateQueries({ queryKey: getListSnippetsQueryKey() });
      setLocation(`/app/snippets/${res.id}`);
    } catch (e: unknown) {
      toast.error(apiErrorMessage(e, "Failed to create snippet"));
    }
  };

  return (
    <div className="flex-1 overflow-auto p-8 bg-background" data-testid="snippet-new-page">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => setLocation("/app/snippets")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">New snippet</h1>
            <p className="text-muted-foreground">
              Define a reusable HTML fragment templates can embed via{" "}
              <code className="text-xs bg-muted px-1 py-0.5 rounded">{`{{> slug}}`}</code>.
            </p>
          </div>
        </div>

        <Card>
          <CardContent className="pt-6">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g. Promo Banner"
                          data-testid="snippet-name-input"
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        The slug is auto-derived (e.g. "Promo Banner" →
                        <code> promo-banner</code>) and used in the
                        <code> {`{{> slug}}`}</code> reference.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description (optional)</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Short hint about when to use this snippet"
                          data-testid="snippet-description-input"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="bodyHtml"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Body HTML</FormLabel>
                      <FormControl>
                        <Textarea
                          rows={10}
                          className="font-mono text-sm"
                          placeholder={`<p>Hello {{firstName}}</p>`}
                          data-testid="snippet-body-input"
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        Use <code>{`{{var}}`}</code> placeholders just like in a
                        template. They'll resolve against the host template's
                        variable scope at render time.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <SnippetVariablesEditor
                  fields={fields}
                  append={append}
                  remove={remove}
                  form={form}
                />

                <div className="flex justify-end gap-2">
                  <Link href="/app/snippets">
                    <Button type="button" variant="ghost">
                      Cancel
                    </Button>
                  </Link>
                  <Button
                    type="submit"
                    disabled={createMutation.isPending}
                    data-testid="snippet-create-submit"
                  >
                    {createMutation.isPending && (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    )}
                    Create snippet
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Reusable variable editor used by both the create and detail/edit
// pages. Each row mirrors the template variables editor so users get
// the same form vocabulary across templates and snippets.
export function SnippetVariablesEditor({
  fields,
  append,
  remove,
  form,
}: {
  fields: { id: string }[];
  append: (v: {
    key: string;
    label: string;
    type: "text" | "textarea" | "url" | "email" | "number" | "date";
    required: boolean;
    defaultValue: string | null;
    placeholder: string | null;
  }) => void;
  remove: (idx: number) => void;
  form: ReturnType<typeof useForm<z.infer<typeof formSchema>>>;
}) {
  return (
    <div className="space-y-3" data-testid="snippet-variables-editor">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium">Variables</p>
          <p className="text-xs text-muted-foreground">
            Optional. Variables declared here are resolved from the host
            template's scope, so a snippet that uses{" "}
            <code>{`{{firstName}}`}</code> picks up whatever the surrounding
            template provides.
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() =>
            append({
              key: "",
              label: "",
              type: "text",
              required: false,
              defaultValue: "",
              placeholder: "",
            })
          }
          data-testid="snippet-add-variable"
        >
          <Plus className="h-4 w-4 mr-1" /> Add variable
        </Button>
      </div>
      {fields.map((f, idx) => (
        <div
          key={f.id}
          className="grid grid-cols-12 gap-2 items-end border rounded-md p-3"
        >
          <FormField
            control={form.control}
            name={`variables.${idx}.key`}
            render={({ field }) => (
              <FormItem className="col-span-3">
                <FormLabel className="text-xs">Key</FormLabel>
                <FormControl>
                  <Input className="h-8" {...field} />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={`variables.${idx}.label`}
            render={({ field }) => (
              <FormItem className="col-span-3">
                <FormLabel className="text-xs">Label</FormLabel>
                <FormControl>
                  <Input className="h-8" {...field} />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={`variables.${idx}.type`}
            render={({ field }) => (
              <FormItem className="col-span-2">
                <FormLabel className="text-xs">Type</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger className="h-8">
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {VARIABLE_TYPE_VALUES.map((v) => (
                      <SelectItem key={v} value={v}>{VARIABLE_TYPE_LABELS[v]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={`variables.${idx}.defaultValue`}
            render={({ field }) => (
              <FormItem className="col-span-2">
                <FormLabel className="text-xs">Default</FormLabel>
                <FormControl>
                  <Input className="h-8" {...field} value={field.value ?? ""} />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={`variables.${idx}.required`}
            render={({ field }) => (
              <FormItem className="col-span-1 flex flex-col items-center">
                <FormLabel className="text-xs">Req</FormLabel>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <div className="col-span-1 flex justify-end">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => remove(idx)}
              aria-label="Remove variable"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}
